/** Editable placement map: each website page is a stack of section strips
 *  with frames on them (backend: partnerships/placement_map.py). */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';

export interface MapFrame {
  id: number;
  section: number;
  /** Placements & prices line this spot sells (id), with its key/name/type. */
  rate: number | null;
  rate_key: string | null;
  rate_label: string | null;
  rate_asset_type: string | null;
  label: string;
  /** Percent of the section strip. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Dashed: only shows on the site once booked. */
  ghost: boolean;
  /** Marks one slot inside a bigger frame — drawn stronger, tagged "1 slot". */
  one_slot: boolean;
  views_per_week: number | null;
  clicks_per_week: number | null;
  sort_order: number;
}

export interface MapSection {
  id: number;
  page: number;
  key: string;
  label: string;
  image: string;
  width: number;
  height: number;
  sort_order: number;
  visible_by_default: boolean;
  live: boolean;
  frames: MapFrame[];
}

export interface MapPage {
  id: number;
  key: 'home' | 'collection' | 'product' | string;
  title: string;
  sort_order: number;
  sections: MapSection[];
}

/** A campaign's version of a section (order = array order). */
export interface CampaignMapSection {
  id: number;
  key: string;
  label: string;
  live: boolean;
  image: string;
  width: number;
  height: number;
  own_image: boolean;
  hidden: boolean;
  frames: MapFrame[];
}
export interface CampaignMapPage {
  id: number;
  key: string;
  title: string;
  sections: CampaignMapSection[];
}

const PAGES = ['placement-map', 'pages'];

export const useMapPages = () =>
  useQuery<MapPage[]>({
    queryKey: PAGES,
    queryFn: async () => (await axios.get('api/placement-map/pages/')).data,
  });

export const useMapMutations = () => {
  const qc = useQueryClient();
  const done = () => {
    qc.invalidateQueries({ queryKey: PAGES });
    qc.invalidateQueries({ queryKey: ['partnership'] });
  };
  return {
    addSection: useMutation({
      mutationFn: async (body: { page: number; label: string; image: string; width: number; height: number; sort_order?: number; live?: boolean }) =>
        (await axios.post('api/placement-map/sections/', body)).data as MapSection,
      onSuccess: done,
    }),
    patchSection: useMutation({
      mutationFn: async ({ id, ...body }: { id: number } & Partial<Omit<MapSection, 'id' | 'frames'>>) =>
        (await axios.patch(`api/placement-map/sections/${id}/`, body)).data as MapSection,
      onSuccess: done,
    }),
    deleteSection: useMutation({
      mutationFn: async (id: number) => axios.delete(`api/placement-map/sections/${id}/`),
      onSuccess: done,
    }),
    reorder: useMutation({
      mutationFn: async ({ page, ids }: { page: number; ids: number[] }) =>
        (await axios.post(`api/placement-map/pages/${page}/reorder/`, { ids })).data as MapPage,
      onSuccess: done,
    }),
    addFrame: useMutation({
      mutationFn: async (body: Partial<MapFrame> & { section: number }) =>
        (await axios.post('api/placement-map/frames/', body)).data as MapFrame,
      onSuccess: done,
    }),
    patchFrame: useMutation({
      mutationFn: async ({ id, ...body }: { id: number } & Partial<MapFrame>) =>
        (await axios.patch(`api/placement-map/frames/${id}/`, body)).data as MapFrame,
      onSuccess: done,
    }),
    deleteFrame: useMutation({
      mutationFn: async (id: number) => axios.delete(`api/placement-map/frames/${id}/`),
      onSuccess: done,
    }),
  };
};

const campaignKey = (campaignId: string) => ['partnership', campaignId, 'map'] as const;

export const useCampaignMap = (campaignId: string | undefined) =>
  useQuery<CampaignMapPage[]>({
    queryKey: campaignKey(campaignId ?? ''),
    enabled: !!campaignId,
    queryFn: async () => (await axios.get(`api/partnerships/${campaignId}/map/`)).data,
  });

export const useCampaignMapMutations = (campaignId: string) => {
  const qc = useQueryClient();
  const set = (data: CampaignMapPage[]) => qc.setQueryData(campaignKey(campaignId), data);
  return {
    savePage: useMutation({
      mutationFn: async (body: { page: string; sections: { id: number; hidden: boolean; image?: string; width?: number; height?: number }[] }) =>
        (await axios.put(`api/partnerships/${campaignId}/map/`, body)).data as CampaignMapPage[],
      onSuccess: set,
    }),
    resetPage: useMutation({
      mutationFn: async (page: string) =>
        (await axios.post(`api/partnerships/${campaignId}/map/reset/`, { page })).data as CampaignMapPage[],
      onSuccess: set,
    }),
  };
};

/** Read a picture file, scale it to max 2400px wide (JPEG) and return it
 *  as a data URL with its size. 2400px keeps text sharp on Retina screens
 *  (it was 1200px, which made newsletter mockups blurry — Doreen,
 *  2026-10-22); `toJpeg` keeps it under the server's size limit. Tries three ways to
 *  decode it (createImageBitmap, an object URL, a data URL), because which
 *  one works differs per browser and per file (very large or tall
 *  screenshots, files straight from the macOS thumbnail…). Very tall
 *  pictures are scaled down further so the canvas stays within browser
 *  limits. */
type Drawable = { el: CanvasImageSource; w: number; h: number; done: () => void };

const viaBitmap = async (file: File): Promise<Drawable> => {
  const bmp = await createImageBitmap(file);
  return { el: bmp, w: bmp.width, h: bmp.height, done: () => bmp.close() };
};
const viaImg = (src: string, cleanup = () => {}): Promise<Drawable> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ el: img, w: img.naturalWidth, h: img.naturalHeight, done: cleanup });
    img.onerror = () => { cleanup(); reject(new Error('decode')); };
    img.src = src;
  });
const viaObjectUrl = (file: File) => {
  const url = URL.createObjectURL(file);
  return viaImg(url, () => URL.revokeObjectURL(url));
};
const viaDataUrl = (file: File) =>
  new Promise<Drawable>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read'));
    reader.onload = () => viaImg(String(reader.result)).then(resolve, reject);
    reader.readAsDataURL(file);
  });

export const readPicture = async (file: File): Promise<{ image: string; width: number; height: number }> => {
  const kind = (file.type || file.name.split('.').pop() || '').toLowerCase();
  if (/heic|heif/.test(kind)) throw new Error('HEIC pictures can\'t be read here — save it as PNG or JPG first.');
  if (file.type && !file.type.startsWith('image/')) throw new Error(`“${file.name}” is not a picture — use a PNG or JPG.`);
  let pic: Drawable | null = null;
  for (const way of [viaBitmap, viaObjectUrl, viaDataUrl]) {
    try {
      pic = await way(file);
      if (pic.w && pic.h) break;
    } catch {
      pic = null;
    }
  }
  if (!pic || !pic.w || !pic.h) {
    throw new Error(`Your browser couldn't read “${file.name}”. On a Mac this usually means it isn't allowed to open files in that folder (e.g. Documents). Use “paste from clipboard” instead, or move the file to Desktop or Downloads.`);
  }
  // Max 2400px wide, and at most ~12 million pixels in total.
  let w = Math.min(2400, pic.w);
  let h = Math.round((pic.h * w) / pic.w);
  const maxPixels = 12_000_000;
  if (w * h > maxPixels) {
    const f = Math.sqrt(maxPixels / (w * h));
    w = Math.floor(w * f);
    h = Math.floor(h * f);
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    pic.done();
    throw new Error('Could not read the picture.');
  }
  ctx.fillStyle = '#fff'; // transparent PNG areas become white, not black
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(pic.el, 0, 0, w, h);
  pic.done();
  return toJpeg(canvas);
};

/** The canvas as a JPEG data URL, as sharp as fits under the server's limit
 *  (6M characters): lower the quality step by step, then shrink the picture. */
const MAX_CHARS = 5_800_000;
export const toJpeg = (canvas: HTMLCanvasElement): { image: string; width: number; height: number } => {
  let c = canvas;
  for (;;) {
    for (const q of [0.92, 0.85, 0.75]) {
      const image = c.toDataURL('image/jpeg', q);
      if (image.length <= MAX_CHARS) return { image, width: c.width, height: c.height };
    }
    const smaller = document.createElement('canvas');
    smaller.width = Math.max(1, Math.round(c.width * 0.8));
    smaller.height = Math.max(1, Math.round(c.height * 0.8));
    const ctx = smaller.getContext('2d');
    if (!ctx) throw new Error('Could not read the picture.');
    ctx.drawImage(c, 0, 0, smaller.width, smaller.height);
    c = smaller;
  }
};

/** The picture on the clipboard (e.g. a screenshot taken with
 *  Cmd+Ctrl+Shift+4), as a File for readPicture — no file to save or pick.
 *  The browser asks once for permission to read the clipboard. */
export const pastedPicture = async (): Promise<File> => {
  if (!navigator.clipboard?.read) throw new Error('This browser can’t paste pictures here — upload the file instead.');
  let items: ClipboardItems;
  try {
    items = await navigator.clipboard.read();
  } catch {
    throw new Error('mosy may not read the clipboard — allow it when the browser asks, or upload the file instead.');
  }
  for (const item of items) {
    const type = item.types.find((t) => t.startsWith('image/'));
    if (type) {
      const blob = await item.getType(type);
      return new File([blob], 'pasted.png', { type });
    }
  }
  throw new Error('There’s no picture on the clipboard. Take a screenshot with Cmd+Ctrl+Shift+4 (it goes to the clipboard), then paste.');
};
